import { Copy, FlaskConical, Library, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CodeEditor } from "../../../components/CodeEditor";
import { Button, ErrorNote, cx, toast } from "../../../components/ui";
import { api } from "../../../lib/api";
import { useConnections } from "../../../lib/connections";
import { allSteps, fnName, type AutomationFunction, type Definition, type Issue } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
const small = "h-8 rounded-lg border border-line bg-surface px-2 text-sm";
const KEY = /^[A-Za-z_$][\w$]{0,59}(\([^()]{0,200}\))?$/;

interface LibEntry {
  name: string;
  key: string;
  description: string;
  current: number;
  updatedAt: string;
}
interface LibFunction extends LibEntry {
  version: number;
  code: string;
  versions: { version: number; at: string; by: string; note?: string }[];
  canEdit: boolean;
}
interface TryResult {
  ok: boolean;
  result?: unknown;
  error?: string;
  logs: string[];
  alerts: string[];
  changed: Record<string, string>;
  skip: boolean;
  pause?: string;
  proceed: boolean;
  stalled: boolean;
  inforCalls: { className: string; kind: string; ms: number; ok: boolean }[];
  ms: number;
}

/** Which steps use a function: before sending, after the step, or as its Done when check. */
export function functionUses(def: Definition, name: string) {
  const out: { step: string; how: string }[] = [];
  for (const s of allSteps(def)) {
    if ((s.preFunctions ?? []).some((c) => fnName(c) === name)) out.push({ step: s.name, how: "before sending" });
    if ((s.postFunctions ?? []).some((c) => fnName(c) === name)) out.push({ step: s.name, how: "after the step" });
    if (s.verify.method === "custom" && fnName(s.verify.function) === name) out.push({ step: s.name, how: "Done when" });
    for (const f of s.infor?.fields ?? []) if (f.value.kind === "function" && fnName(f.value.call) === name) out.push({ step: s.name, how: `for field ${f.name}` });
  }
  // Called from another function (get_ISOTime() from buildWorkTitle(val)).
  const word = new RegExp(`\\b${name.replace(/[$]/g, "\\$")}\\b`);
  for (const f of def.functions ?? []) if (fnName(f.key) !== name && word.test(f.code)) out.push({ step: fnName(f.key), how: "called" });
  return out;
}

/**
 * Functions (docs/phase5-plan.md §4): the automation's own JavaScript, run in the sandbox before or after steps or as
 * a step's Done when check, and the tenant library every automation can call. Try it runs one against sample values.
 */
export function FunctionsEditor({ id, def, issues, onChange }: { id: string; def: Definition; issues: Issue[]; onChange: (d: Definition) => void }) {
  const fns = def.functions ?? [];
  const [selected, setSelected] = useState<{ kind: "own"; key: string } | { kind: "lib"; name: string } | null>(fns[0] ? { kind: "own", key: fns[0].key } : null);
  const [lib, setLib] = useState<LibEntry[]>([]);
  const [libError, setLibError] = useState<string | null>(null);
  const loadLib = useCallback(async () => {
    try {
      setLib((await api<{ functions: LibEntry[] }>("/apps/ja/functions")).functions);
      setLibError(null);
    } catch (e) {
      setLibError((e as Error).message);
    }
  }, []);
  useEffect(() => void loadLib(), [loadLib]);

  const setFns = (next: AutomationFunction[]) => onChange({ ...def, functions: next });
  const own = selected?.kind === "own" ? fns.find((f) => f.key === selected.key) : undefined;
  const add = () => {
    let n = fns.length + 1;
    while (fns.some((f) => fnName(f.key) === `newFunction${n}`)) n++;
    const key = `newFunction${n}()`;
    setFns([...fns, { key, code: "// Runs in the sandbox. Before a step: call lib_popfunction() to carry on.\nlib_popfunction()\n" }]);
    setSelected({ kind: "own", key });
  };
  const copy = (f: AutomationFunction) => {
    let n = 2;
    while (fns.some((x) => fnName(x.key) === `${fnName(f.key)}${n}`)) n++;
    const key = f.key.replace(/^[^(]+/, `${fnName(f.key)}${n}`);
    setFns([...fns, { ...f, key }]);
    setSelected({ kind: "own", key });
  };

  return (
    <div className={cx("grid gap-4", selected ? "lg:grid-cols-[18rem_minmax(0,1fr)]" : "")}>
      <div className="space-y-3">
        <div className="rounded-xl border border-line bg-surface divide-y divide-line">
          <div className="px-3 py-2 text-xs font-semibold uppercase text-muted">This automation</div>
          {fns.map((f) => {
            const bad = issues.some((i) => i.where === `function ${f.key}` && i.level === "error");
            const uses = functionUses(def, fnName(f.key)).length;
            return (
              <button key={f.key} type="button" aria-pressed={selected?.kind === "own" && selected.key === f.key} onClick={() => setSelected({ kind: "own", key: f.key })} className={cx("w-full text-left px-3 py-2 hover:bg-surface-2", selected?.kind === "own" && selected.key === f.key && "bg-accent-soft")}>
                <span className={cx("block font-mono text-sm truncate", bad ? "text-bad" : "text-ink")}>{f.key}</span>
                <span className="block text-xs text-muted truncate">{f.desc || (uses ? `used by ${uses} step${uses === 1 ? "" : "s"}` : "not used by a step")}</span>
              </button>
            );
          })}
          {!fns.length && <p className="px-3 py-2 text-sm text-muted">No functions yet.</p>}
        </div>
        <Button onClick={add}>
          <Plus className="size-4" aria-hidden /> Function
        </Button>

        <div className="rounded-xl border border-line bg-surface divide-y divide-line">
          <div className="px-3 py-2 text-xs font-semibold uppercase text-muted flex items-center gap-1.5">
            <Library className="size-3.5" aria-hidden /> Library (every automation)
          </div>
          {lib.map((f) => {
            const hidden = fns.some((x) => fnName(x.key) === f.name);
            return (
              <button key={f.name} type="button" aria-pressed={selected?.kind === "lib" && selected.name === f.name} onClick={() => setSelected({ kind: "lib", name: f.name })} className={cx("w-full text-left px-3 py-2 hover:bg-surface-2", selected?.kind === "lib" && selected.name === f.name && "bg-accent-soft")}>
                <span className="block font-mono text-sm truncate">{f.key}</span>
                <span className="block text-xs text-muted truncate">
                  v{f.current}
                  {hidden ? " · this automation's own version wins" : f.description ? ` · ${f.description}` : ""}
                </span>
              </button>
            );
          })}
          {!lib.length && !libError && <p className="px-3 py-2 text-sm text-muted">The library is empty.</p>}
          {libError && <p className="px-3 py-2 text-sm text-bad">{libError}</p>}
        </div>
        <Button onClick={() => setSelected({ kind: "lib", name: "" })}>
          <Plus className="size-4" aria-hidden /> Library function
        </Button>
      </div>

      {own && (
        <OwnFunction
          key={own.key}
          id={id}
          def={def}
          fn={own}
          issues={issues.filter((i) => i.where === `function ${own.key}`)}
          libHas={lib.some((l) => l.name === fnName(own.key))}
          onChange={(f) => {
            setFns(fns.map((x) => (x.key === own.key ? f : x)));
            if (f.key !== own.key) setSelected({ kind: "own", key: f.key });
          }}
          onCopy={() => copy(own)}
          onDelete={() => {
            setFns(fns.filter((x) => x.key !== own.key));
            setSelected(null);
          }}
          onClose={() => setSelected(null)}
        />
      )}
      {selected?.kind === "lib" && <LibraryFunction key={selected.name || "new"} id={id} def={def} name={selected.name} onSaved={(name) => (void loadLib(), setSelected({ kind: "lib", name }))} onDeleted={() => (void loadLib(), setSelected(null))} onClose={() => setSelected(null)} />}
    </div>
  );
}

function OwnFunction({ id, def, fn, issues, libHas, onChange, onCopy, onDelete, onClose }: { id: string; def: Definition; fn: AutomationFunction; issues: Issue[]; libHas: boolean; onChange: (f: AutomationFunction) => void; onCopy: () => void; onDelete: () => void; onClose: () => void }) {
  const [key, setKey] = useState(fn.key);
  const uses = functionUses(def, fnName(fn.key));
  const others = (def.functions ?? []).filter((f) => f.key !== fn.key);
  const keyProblem = !KEY.test(key.trim()) ? "Letters, digits and _, then optional (params), e.g. buildTitle(val)" : others.some((f) => fnName(f.key) === fnName(key)) ? "Another function has this name" : null;
  return (
    <div className="rounded-xl border border-line bg-surface p-4 space-y-4 min-w-0">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 space-y-1">
          <label className="block">
            <span className="text-sm text-ink-2 font-medium">Name and parameters</span>
            <input className={cx(input, "font-mono")} value={key} onChange={(e) => setKey(e.target.value)} onBlur={() => !keyProblem && key.trim() !== fn.key && onChange({ ...fn, key: key.trim() })} aria-invalid={!!keyProblem} />
          </label>
          {keyProblem && <p className="text-xs text-bad">{keyProblem}</p>}
          {key.trim() !== fn.key && !keyProblem && uses.length > 0 && <p className="text-xs text-warn">Steps call it by its old name: rename those too.</p>}
        </div>
        <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2" aria-label="Copy function" title="Copy" onClick={onCopy}>
          <Copy className="size-4" />
        </button>
        <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2 disabled:opacity-40" aria-label="Delete function" title={uses.length ? "Used by steps" : "Delete"} disabled={uses.length > 0} onClick={onDelete}>
          <Trash2 className="size-4" />
        </button>
        <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2" aria-label="Close" onClick={onClose}>
          <X className="size-4" />
        </button>
      </div>
      <label className="block">
        <span className="text-sm text-ink-2 font-medium">Description</span>
        <input className={input} value={fn.desc ?? ""} onChange={(e) => onChange({ ...fn, desc: e.target.value || undefined })} placeholder="What it does (optional)" />
      </label>
      {libHas && <p className="text-xs text-muted">The library has a function with this name too: this automation's own version is the one its steps run.</p>}
      <CodeEditor docKey={fn.key} label={`Code of ${fn.key}`} value={fn.code} onChange={(code) => onChange({ ...fn, code })} />
      {fn.source !== undefined && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted">As imported from the function file (the importer dropped legacy log and parameter writes)</summary>
          <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-surface-2 p-2 font-mono whitespace-pre-wrap break-words">{fn.source}</pre>
        </details>
      )}
      {issues.map((i) => (
        <p key={i.message} className={cx("text-sm", i.level === "error" ? "text-bad" : "text-warn")}>
          {i.message}
        </p>
      ))}
      <p className="text-xs text-muted">
        {uses.length ? `Used ${uses.map((u) => `${u.how} by ${u.step}`).join("; ")}.` : "No step uses it yet: add it to a step's Functions or Done when."} Helpers: getObjectVal, userparmobject, lib_popfunction, queryInfor, infor.list, skipJob, log… (the legacy names work as before).
      </p>
      <TryIt id={id} def={def} fnKey={fn.key} />
    </div>
  );
}

function LibraryFunction({ id, def, name, onSaved, onDeleted, onClose }: { id: string; def: Definition; name: string; onSaved: (name: string) => void; onDeleted: () => void; onClose: () => void }) {
  const [f, setF] = useState<LibFunction | null>(null);
  const [key, setKey] = useState("newLibraryFunction()");
  const [desc, setDesc] = useState("");
  const [code, setCode] = useState("// Shared by every automation.\n");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    if (!name) return;
    void api<LibFunction>(`/apps/ja/functions/${encodeURIComponent(name)}`).then((x) => {
      setF(x);
      setKey(x.key);
      setDesc(x.description);
      setCode(x.code);
    }, (e) => setError((e as Error).message));
  }, [name]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const n = fnName(key);
      const saved = await api<LibFunction>(`/apps/ja/functions/${encodeURIComponent(n)}`, { method: "PUT", body: { key: key.trim(), code, description: desc, note: note || undefined, ...(f && n === f.name ? { expect: f.current } : {}) } });
      toast(`Saved ${saved.name} v${saved.current} to the library`);
      setNote("");
      onSaved(saved.name);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const changed = !f || key.trim() !== f.key || code !== f.code || desc !== f.description;
  const uses = functionUses(def, fnName(key));
  return (
    <div className="rounded-xl border border-line bg-surface p-4 space-y-4 min-w-0">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted flex items-center gap-1">
            <Library className="size-3.5" aria-hidden /> Library function{f ? ` · v${f.current}, saved by ${f.versions[f.versions.length - 1]?.by ?? "?"}` : " · new"}
          </p>
          <label className="block mt-1">
            <span className="text-sm text-ink-2 font-medium">Name and parameters</span>
            <input className={cx(input, "font-mono")} value={key} onChange={(e) => setKey(e.target.value)} disabled={!!f} aria-invalid={!KEY.test(key.trim())} />
          </label>
        </div>
        {f && (
          <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2" aria-label="Remove from the library" title="Remove from the library" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="size-4" />
          </button>
        )}
        <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2" aria-label="Close" onClick={onClose}>
          <X className="size-4" />
        </button>
      </div>
      <label className="block">
        <span className="text-sm text-ink-2 font-medium">Description</span>
        <input className={input} value={desc} onChange={(e) => setDesc(e.target.value)} />
      </label>
      <CodeEditor docKey={`lib:${name}:${f?.current ?? 0}`} label={`Code of library function ${key}`} value={code} onChange={setCode} />
      <div className="flex flex-wrap items-center gap-2">
        <input className={cx(small, "flex-1 min-w-48")} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed (optional)" />
        <Button variant="primary" busy={busy} disabled={!changed || !KEY.test(key.trim())} onClick={() => void save()}>
          {f ? `Save version ${f.current + 1}` : "Add to the library"}
        </Button>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <p className="text-xs text-muted">
        Every automation can call it. A run uses the version that was current when it started. {uses.length ? `This automation uses it ${uses.map((u) => `${u.how} by ${u.step}`).join("; ")}.` : ""}
      </p>
      {f && !changed && <TryIt id={id} def={def} fnKey={f.key} />}
      {confirmDelete && f && (
        <div className="rounded-lg border border-bad/40 bg-bad/5 p-3 text-sm space-y-2">
          <p>Remove {f.name} from the library? Runs that already started keep their version; new runs and saves will report it missing wherever it's used.</p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={async () => {
                await api(`/apps/ja/functions/${encodeURIComponent(f.name)}`, { method: "DELETE" });
                toast(`Removed ${f.name} from the library`);
                onDeleted();
              }}
            >
              Remove
            </Button>
            <Button onClick={() => setConfirmDelete(false)}>Keep it</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Runs a function in the sandbox against sample prompt values (the saved draft's code). Infor reads only. */
function TryIt({ id, def, fnKey }: { id: string; def: Definition; fnKey: string }) {
  const { apps } = useConnections();
  const conns = (apps.ja?.options ?? []).filter((o) => o.usable && !o.production);
  const [open, setOpen] = useState(false);
  const [args, setArgs] = useState("");
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(def.prompts.map((p) => [p.code, p.default ?? ""])));
  const [connId, setConnId] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<TryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const params = useMemo(() => (/\(([^)]*)\)/.exec(fnKey)?.[1] ?? (fnKey.includes("(") ? "" : "val")).split(",").map((p) => p.trim()).filter(Boolean), [fnKey]);
  const parseArgs = () => {
    if (!args.trim()) return [];
    try {
      const v = JSON.parse(`[${args}]`);
      return Array.isArray(v) ? v : [v];
    } catch {
      return [args];
    }
  };
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setOut(await api<TryResult>(`/apps/ja/automations/${id}/functions/try`, { body: { key: fnKey, args: parseArgs(), values, connId: connId || undefined } }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!open)
    return (
      <Button onClick={() => setOpen(true)}>
        <FlaskConical className="size-4" aria-hidden /> Try it
      </Button>
    );
  return (
    <div className="rounded-lg border border-line bg-surface-2 p-3 space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <FlaskConical className="size-4 text-accent-text" aria-hidden />
        <span className="font-medium">Try it</span>
        <span className="text-xs text-muted">runs the saved draft's code; nothing is saved and Infor is only read</span>
      </div>
      <label className="block">
        <span className="text-ink-2 font-medium">Arguments{params.length ? ` (${params.join(", ")})` : ""}</span>
        <input className={cx(input, "font-mono")} value={args} onChange={(e) => setArgs(e.target.value)} placeholder={params.length ? `e.g. "Payroll"${params.length > 1 ? ", 2" : ""}` : "none"} />
      </label>
      <details>
        <summary className="cursor-pointer text-ink-2">Sample prompt values</summary>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {def.prompts.map((p) => (
            <label key={p.code} className="block">
              <span className="text-xs text-muted font-mono">{p.code}</span>
              <input className={small + " w-full"} value={values[p.code] ?? ""} onChange={(e) => setValues({ ...values, [p.code]: e.target.value })} placeholder={p.kind === "date" ? "MM/DD/YYYY" : ""} />
            </label>
          ))}
        </div>
      </details>
      <label className="block">
        <span className="text-ink-2 font-medium">Read Infor from</span>
        <select className={input} value={connId} onChange={(e) => setConnId(e.target.value)}>
          <option value="">No connection (Infor calls fail)</option>
          {conns.map((c) => (
            <option key={c.connId} value={c.connId}>
              {c.name} ({c.envLabel})
            </option>
          ))}
        </select>
      </label>
      <Button variant="primary" busy={busy} onClick={() => void run()}>
        Run {fnName(fnKey)}
      </Button>
      {error && <ErrorNote>{error}</ErrorNote>}
      {out && (
        <div className="space-y-1.5">
          <p className={out.ok ? "text-good" : "text-bad"}>
            {out.ok ? "Returned" : "Failed"} in {out.ms} ms{out.ok && out.result !== null && out.result !== undefined ? ":" : out.ok ? " (no value)" : `: ${out.error}`}
          </p>
          {out.ok && out.result !== null && out.result !== undefined && <pre className="rounded bg-surface border border-line p-2 text-xs whitespace-pre-wrap break-all max-h-60 overflow-auto">{typeof out.result === "string" ? out.result : JSON.stringify(out.result, null, 2)}</pre>}
          {Object.keys(out.changed).length > 0 && <p>Set: {Object.entries(out.changed).map(([k, v]) => `${k} = ${v}`).join(", ")}</p>}
          {out.skip && <p>Called skipJob(): the step would be skipped.</p>}
          {out.pause && <p>Would pause: {out.pause}</p>}
          {out.proceed && <p>Reached addJob(): the step would be sent.</p>}
          {out.stalled && <p className="text-warn">Stopped with functions still waiting on the stack (nothing called lib_popfunction()).</p>}
          {out.alerts.map((a) => (
            <p key={a} className="text-warn">
              Alert: {a}
            </p>
          ))}
          {out.inforCalls.length > 0 && <p className="text-muted">Infor: {out.inforCalls.map((c) => `${c.className} (${c.kind}, ${c.ms} ms${c.ok ? "" : ", failed"})`).join(", ")}</p>}
          {out.logs.length > 0 && <pre className="rounded bg-surface border border-line p-2 text-xs whitespace-pre-wrap max-h-40 overflow-auto">{out.logs.join("\n")}</pre>}
        </div>
      )}
    </div>
  );
}
